<template>
  <div class="catalog-slider-fi">
    <div
      :key="refreshCount"
      :class="{
        'catalog-slider-fi__container': true,
        'catalog-slider-fi__container--visible': true,
        'catalog-slider-fi__container--single-slide': thumbnails.length === 1,
        'catalog-slider-fi__container--duo-slide': thumbnails.length === 2,
      }"
    >
      <carousel-fi
        ref="carousel"
        class="catalog-slider-fi__carousel"
        :settings="!isMD ? mainSettings : mainSettingsMD"
        :items="thumbnails"
        :no-arrows="!isMD"
        :thumbnail-carousel-ref="thumbnailCarouselRef"
        theme="catalog-slider"
        @swipe="notifySwipeToCatalogSlides"
      >
        <carousel-slide-fi
          v-for="(item, index) in thumbnails"
          :key="index"
          theme="catalog-slider"
        >
          <catalog-slider-item-fi
            ref="catalogItem"
            :item="item"
            :item-name="itemName"
            :zoomable="isZoomable && !item.videoId"
            :eager="index === 0"
            @zoom="openZoom"
          />
        </carousel-slide-fi>
      </carousel-fi>

      <div class="catalog-slider-fi__thumbnail-wrapper">
        <carousel-fi
          ref="thumbnails"
          class="catalog-slider-fi__carousel catalog-slider-fi__carousel--thumbnail"
          :settings="thumbnailSettings"
          theme="catalog-slider-thumbnail"
          no-arrows
        >
          <carousel-slide-fi
            v-for="(item, index) in carouselThumbnails"
            :key="index"
            theme="catalog-slider-thumbnail"
          >
            <catalog-slider-item-fi
              :item="item"
              :is-thumbnail="true"
              :eager="index === 0"
            />
          </carousel-slide-fi>
        </carousel-fi>
      </div>
    </div>
    <dialog-modal-fi
      v-if="isZoomable"
      ref="zoom"
      class="catalog-slider-fi__carousel-zoom"
      lazy
      full-screen
      @close="destroyZoom"
    >
      <template #header>
        <div class="catalog-slider-fi__carousel-zoom__header">
          <div class="catalog-slider-fi__carousel-zoom__header__subtitle">{{ headerSubtitle }}</div>
          <div class="catalog-slider-fi__carousel-zoom__header__title">{{ headerTitle }}</div>
        </div>
      </template>
      <template #body>
        <div class="catalog-slider-fi__carousel-zoom-wrapper">
          <carousel-fi
            ref="carouselZoom"
            class="catalog-slider-fi__carousel catalog-slider-fi__carousel-zoom"
            :settings="{ ...(!isMD ? mainSettings : mainSettingsMD), autoHeight: true }"
            :items="thumbnails"
            :no-arrows="!isMD"
            theme="catalog-slider"
            @swipe="notifySwipeToZoomCatalogSlides"
          >
            <carousel-slide-fi
              v-for="(item, index) in thumbnails"
              :key="index"
              theme="catalog-slider"
            >
              <catalog-slider-item-fi
                ref="zoomCatalogItem"
                :item="item"
                :item-name="itemName"
                is-zoom
              />
            </carousel-slide-fi>
          </carousel-fi>
        </div>

        <div class="catalog-slider-fi__thumbnail-wrapper catalog-slider-fi__zoom-thumbnail-wrapper">
          <carousel-fi
            ref="thumbnailsZoom"
            :class="{
              'catalog-slider-fi__carousel': true,
              'catalog-slider-fi__carousel--thumbnail': true,
              'catalog-slider-fi__carousel--thumbnail-zoom': true,
              'catalog-slider-fi__carousel--single': carouselThumbnails.length === 1,
            }"
            :settings="thumbnailSettingsZoom"
            theme="catalog-slider-thumbnail"
            no-arrows
          >
            <carousel-slide-fi
              v-for="(item, index) in carouselThumbnails"
              :key="index"
              theme="catalog-slider-thumbnail"
            >
              <catalog-slider-item-fi
                :item="item"
                :is-thumbnail="true"
              />
            </carousel-slide-fi>
          </carousel-fi>
        </div>
      </template>
    </dialog-modal-fi>
  </div>
</template>

<script>
import { CarouselFi, CarouselSlideFi, DialogModalFi } from 'atoms';

import CatalogSliderItemFi from '@/components/catalog-slider-fi/CatalogSliderItemFi.vue';
import breakpointMixin from '@/mixins/breakpointMixin.vue';
import catalogMixin from '@/mixins/catalogMixin.vue';
import catalogTextListMixin from '@/mixins/catalogTextListMixin.vue';

export default {
  name: 'CatalogSliderFi',
  components: {
    CarouselFi,
    CarouselSlideFi,
    CatalogSliderItemFi,
    DialogModalFi,
  },
  mixins: [catalogMixin, catalogTextListMixin, breakpointMixin],
  inject: ['fields'],
  props: {
    catalogData: {
      type: Object,
      default: null,
    },
    itemName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      thumbnailCarouselRef: null,
      // Type changes do not work in breakpoint settings
      mainSettings: {
        rewind: true,
        pagination: true,
        gap: '24px',
      },
      mainSettingsMD: {
        rewind: true,
        pagination: true, // we have pagination here too and just hide it to be consistent when re-hydrating
        type: 'fade',
        gap: '24px',
      },
      refreshCount: 0,
      isMounted: false,
    };
  },
  computed: {
    isZoomable() {
      return this.isMD && this.fields.enableZoom?.value;
    },
    thumbnails() {
      return this.getFamilyThumbnails(this.catalogData);
    },
    headerSubtitle() {
      const artNr = this.catalogData?.ProductId && Number(this.catalogData.ProductId);
      if (artNr) {
        return `${this.$dict.get('product-List.Table.Article-Number')} ${artNr}`;
      }
      return this.textListValue(this.catalogData.TextList, 'KAT_POS');
    },
    headerTitle() {
      const productName = this.textListValue(this.catalogData?.TextList, 'ECOMLANG');
      if (productName) {
        return productName;
      }
      return this.textListValue(this.catalogData.TextList, 'BEZEICHNUNG');
    },
    carouselThumbnails() {
      if (this.isMounted || this.thumbnails.length < 3) {
        return this.thumbnails;
      }
      // center first item
      const carouselThumbnails = this.thumbnails.slice();
      carouselThumbnails.unshift(carouselThumbnails.pop());
      return carouselThumbnails;
    },
    thumbnailSettings() {
      const settings = {
        type: 'loop',
        isNavigation: true,
        perPage: 3,
        perMove: 1,
        focus: 'center',
        gap: IS_SSR ? undefined : '24px',
      };

      const { length } = this.thumbnails;
      if (length < 3) {
        if (length === 1) {
          settings.padding = {
            right: '33.33%',
            left: '33.33%',
          };
          settings.gap = '100%';
        } else {
          settings.gap = '33.33%';
        }
        settings.perPage = length;
        settings.focus = 0;
      }

      return settings;
    },
    thumbnailSettingsZoom() {
      return {
        ...this.thumbnailSettings,
        type: 'slide',
        padding: undefined,
        autoWidth: true,
        rewind: false,
        focus: this.carouselThumbnails.length > 4 ? 'center' : 0,
      };
    },
  },
  watch: {
    isMD() {
      this.refresh();
    },
    thumbnails() {
      this.refresh(); // this should only happen in the showroom
    },
  },
  mounted() {
    this.isMounted = true;
    if (!this.$refs.carousel || !this.$refs.thumbnails) {
      return;
    }

    this.syncCarousels();
    setTimeout(() => this.$refs.thumbnails.refresh(), 500); // this is unfortunately required for the youtube-icon to show up
  },
  methods: {
    notifySwipeToCatalogSlides(index) {
      this.$refs.catalogItem.forEach((item) => item.onSwipe(index));
    },
    notifySwipeToZoomCatalogSlides(index) {
      this.$refs.zoomCatalogItem.forEach((item) => item.onSwipe(index));
    },
    async destroyZoom() {
      setTimeout(() => (this.$refs.zoom.hasBeenOpened = false), 300);
    },
    async openZoom() {
      this.$refs.zoom.open();
      await this.$nextTick();
      this.$refs.carouselZoom.$refs.carousel.sync(this.$refs.thumbnailsZoom.$refs.carousel.splide);
      this.$refs.carouselZoom.go(this.$refs.carousel.getIndex());
    },
    syncCarousels() {
      this.thumbnailCarouselRef = this.$refs.thumbnails;
      this.$refs.carousel.$refs.carousel.sync(this.$refs.thumbnails.$refs.carousel.splide);
    },
    async refresh() {
      this.refreshCount += 1;
      await this.$nextTick();
      this.syncCarousels();
    },
  },
};
</script>

<style src="./catalog-slider-fi-unscoped" />

<style scoped src="./catalog-slider-fi" />
